import { AnimatePresence, motion } from "framer-motion";
import { ShoppingCart, Pause, ArrowRight, Trash } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { CashierCartItem } from "./CashierCartItem";
import { CashierCartTotals } from "./CashierCartTotals";
import { CashierCustomerPicker, type CashierCustomer } from "./CashierCustomerPicker";
import type { CashierLabels, CashierOrder } from "./types";

export interface CashierCartPanelProps {
  order: CashierOrder;
  labels: CashierLabels;
  customers?: readonly CashierCustomer[];
  onSelectCustomer?: (c: CashierCustomer) => void;
  onClearCustomer?: () => void;
  onCreateCustomer?: () => void;
  onRemoveLineItem: (lineId: string) => void;
  onUpdateQty: (lineId: string, qty: number) => void;
  onApplyDiscount?: (lineId: string | null) => void;
  onPark?: () => void;
  onVoid?: () => void;
  onOpenTender: () => void;
  isSessionClosed?: boolean;
  locale?: "tr" | "en";
  className?: string;
}

export function CashierCartPanel({
  order, labels, customers, onSelectCustomer, onClearCustomer, onCreateCustomer, onRemoveLineItem, onUpdateQty,
  onApplyDiscount, onPark, onVoid, onOpenTender, isSessionClosed, locale = "tr", className,
}: CashierCartPanelProps) {
  const empty = order.lines.length === 0;
  const lineDiscountTotal = order.lines.reduce((acc, l) => acc + (l.discount?.valueMinor ?? 0), 0);
  const totalDiscount = lineDiscountTotal + order.orderDiscountMinor;

  const payDisabledReason = isSessionClosed
    ? labels.cart.payDisabledSessionClosed
    : empty
      ? labels.cart.payDisabledEmpty
      : null;

  return (
    <aside
      className={cn(
        "flex flex-col h-full bg-biz-surface border-l border-biz-border min-w-0",
        className,
      )}
      aria-label={labels.cart.title}
    >
      {/* Header — customer picker */}
      <div className="px-4 py-3 border-b border-biz-border bg-biz-surface-elevated space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-wider text-biz-foreground-muted">
            {labels.cart.title}
          </h2>
          <span className="font-mono-num text-xs text-biz-foreground-subtle tabular-nums">
            #{order.id.slice(-6)}
          </span>
        </div>
        <CashierCustomerPicker
          customers={customers}
          currentCustomer={
            order.customerId
              ? { id: order.customerId, name: order.customerName ?? labels.cart.guest, petName: order.petName }
              : null
          }
          onSelect={onSelectCustomer}
          onClear={onClearCustomer}
          onCreateNew={onCreateCustomer}
          labels={labels}
        />
      </div>

      {/* Lines (scroll) */}
      <div className="flex-1 min-h-0 overflow-auto biz-scrollbar p-3 space-y-2">
        {empty ? (
          <CartEmpty labels={labels} />
        ) : (
          <AnimatePresence initial={false}>
            {order.lines.map((line) => (
              <CashierCartItem
                key={line.lineId}
                line={line}
                onQtyChange={onUpdateQty}
                onRemove={onRemoveLineItem}
                onOpenLineActions={onApplyDiscount ? (id) => onApplyDiscount(id) : undefined}
                labels={labels}
                locale={locale}
              />
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Totals */}
      {!empty && (
        <CashierCartTotals
          subtotalMinor={order.subtotalMinor}
          discountMinor={totalDiscount}
          taxMinor={order.taxMinor}
          totalMinor={order.totalMinor}
          currencyPrefix={labels.currencyPrefix}
          labels={labels}
          locale={locale}
        />
      )}

      {/* Action bar */}
      <div className="p-3 border-t border-biz-border bg-biz-surface-elevated space-y-2">
        {!empty && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onVoid}
              className={cn(
                "h-11 px-3 rounded-biz-btn text-sm font-semibold",
                "border border-biz-border text-biz-foreground-muted hover:text-biz-danger hover:bg-biz-danger-subtle hover:border-biz-danger-border",
                "transition-colors biz-focus-ring inline-flex items-center gap-1.5",
              )}
            >
              <Trash className="h-4 w-4" />
              {labels.cart.void}
            </button>
            <button
              type="button"
              onClick={onPark}
              className={cn(
                "h-11 flex-1 rounded-biz-btn text-sm font-semibold",
                "border border-biz-border text-biz-foreground hover:bg-biz-muted",
                "transition-colors biz-focus-ring inline-flex items-center justify-center gap-1.5",
              )}
            >
              <Pause className="h-4 w-4" />
              {labels.cart.park}
            </button>
          </div>
        )}

        <button
          type="button"
          disabled={!!payDisabledReason}
          onClick={onOpenTender}
          title={payDisabledReason ?? undefined}
          aria-label={labels.cart.pay}
          className={cn(
            "w-full min-h-[56px] rounded-biz-btn font-bold text-base",
            "bg-biz-primary text-biz-primary-foreground biz-shadow-md",
            "hover:bg-biz-primary-hover transition-colors biz-focus-ring",
            "inline-flex items-center justify-center gap-2",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        >
          <span>{labels.cart.pay}</span>
          {!empty && (
            <motion.span
              key={order.totalMinor}
              initial={{ opacity: 0.7, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18 }}
              className="font-mono-num tabular-nums"
            >
              · {labels.currencyPrefix}{(order.totalMinor / 100).toLocaleString(locale === "tr" ? "tr-TR" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </motion.span>
          )}
          <ArrowRight className="h-5 w-5" />
        </button>
      </div>
    </aside>
  );
}

function CartEmpty({ labels }: { labels: CashierLabels }) {
  return (
    <div className="h-full min-h-[260px] flex flex-col items-center justify-center text-center px-6 py-10 text-biz-foreground-muted">
      <div className="h-14 w-14 rounded-full bg-biz-muted flex items-center justify-center mb-3">
        <ShoppingCart className="h-7 w-7 text-biz-foreground-subtle" />
      </div>
      <p className="text-sm font-semibold text-biz-foreground">{labels.cart.emptyTitle}</p>
      <p className="text-xs mt-1 max-w-[220px]">{labels.cart.emptyDescription}</p>
    </div>
  );
}
